import { createSlice, type PayloadAction } from "@reduxjs/toolkit"

interface EmulatorStandalonePanel {
  /** Stable identity for React keys — panels have no content-derived unique id. */
  panelId: string
  deploymentId: string | null
  sessionId: string | null
}

const createEmptyPanel = (): EmulatorStandalonePanel => ({
  panelId: crypto.randomUUID(),
  deploymentId: null,
  sessionId: null
})

interface EmulatorState {
  /** Currently active session per deployment (deploymentId -> sessionId) */
  activeSessionByDeployment: Record<string, string>
  /** Standalone page panels */
  standalone: {
    panels: EmulatorStandalonePanel[]
    sharedInputMode: boolean
  }
  /** Debug panel open state */
  debugPanelOpen: boolean
}

const initialState: EmulatorState = {
  activeSessionByDeployment: {},
  standalone: {
    panels: [createEmptyPanel()],
    sharedInputMode: false
  },
  debugPanelOpen: false
}

const emulatorSlice = createSlice({
  name: "emulator",
  initialState,
  reducers: {
    setActiveSession(state, action: PayloadAction<{ deploymentId: string; sessionId: string }>) {
      state.activeSessionByDeployment[action.payload.deploymentId] = action.payload.sessionId
    },
    clearActiveSession(state, action: PayloadAction<string>) {
      delete state.activeSessionByDeployment[action.payload]
    },

    // Standalone page
    setStandalonePanel(
      state,
      action: PayloadAction<{ index: number; panel: Omit<EmulatorStandalonePanel, "panelId"> }>
    ) {
      const { index, panel } = action.payload
      const existing = state.standalone.panels[index]
      if (existing) {
        // Preserve the panel's stable identity so its React subtree is not remounted.
        state.standalone.panels[index] = { ...panel, panelId: existing.panelId }
      }
    },
    addStandalonePanel(state) {
      if (state.standalone.panels.length < 3) {
        state.standalone.panels.push(createEmptyPanel())
      }
    },
    removeStandalonePanel(state, action: PayloadAction<number>) {
      if (state.standalone.panels.length > 1) {
        state.standalone.panels.splice(action.payload, 1)
      }
    },
    toggleSharedInput(state) {
      state.standalone.sharedInputMode = !state.standalone.sharedInputMode
    },

    // Debug panel
    toggleDebugPanel(state) {
      state.debugPanelOpen = !state.debugPanelOpen
    },
    setDebugPanelOpen(state, action: PayloadAction<boolean>) {
      state.debugPanelOpen = action.payload
    }
  }
})

export const {
  setActiveSession,
  clearActiveSession,
  setStandalonePanel,
  addStandalonePanel,
  removeStandalonePanel,
  toggleSharedInput,
  toggleDebugPanel,
  setDebugPanelOpen
} = emulatorSlice.actions

export const emulatorReducer = emulatorSlice.reducer
